import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import { func, string } from 'prop-types';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  isOriginComplete,
  originFromShippingAddress,
  shippingAddressFromUser,
  normalizeUsStateCode,
  normalizeUsPhone,
} from '../../util/shipping';

import { H3 } from '../Heading/Heading';
import Button from '../Button/Button';
import Form from '../Form/Form';

import ShippingAddressFields from './ShippingAddressFields';
import { saveShippingAddress } from './ShippingAddressForm.duck';
import css from './ShippingAddressForm.module.css';

/**
 * Form that saves the current user's shipping address to protectedData.
 *
 * @param {Object} props
 * @param {Function} props.onSubmit
 * @param {Object} [props.initialValues]
 * @param {boolean} [props.saveInProgress]
 * @param {Object} [props.saveError]
 * @param {boolean} [props.showHeading]
 * @param {boolean} [props.showShippingAddressFormError]
 * @param {string} [props.submitTitle]
 * @param {Function} [props.successCallback]
 * @param {string} [props.className]
 * @param {string} [props.rootClassName]
 * @param {string} [props.formId]
 * @returns {JSX.Element}
 */
export const ShippingAddressFormComponent = props => {
  const intl = useIntl();
  const [submittedValues, setSubmittedValues] = useState({});
  const title = intl.formatMessage({ id: 'ShippingAddressForm.title' });
  const defaultSubmitTitle = intl.formatMessage({
    id: isOriginComplete(originFromShippingAddress(props.initialValues))
      ? 'ShippingAddressForm.updateButton'
      : 'ShippingAddressForm.saveButton',
  });

  return (
    <FinalForm
      {...props}
      enableReinitialize
      initialValuesEqual={isEqual}
      onSubmit={async values => {
        const { onSubmit } = props;
        const response = await onSubmit({
          name: (values.name || '').trim(),
          phone: normalizeUsPhone(values.phone),
          street1: (values.street1 || '').trim(),
          streetNo: (values.streetNo || '').trim(),
          city: (values.city || '').trim(),
          state: normalizeUsStateCode(values.state),
          zip: (values.zip || '').trim(),
          country: values.country || 'US',
        });
        setSubmittedValues(values);
        props.successCallback?.();
        return response;
      }}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId = 'ShippingAddressForm',
          handleSubmit,
          form,
          values,
          saveInProgress,
          saveError,
          showHeading = true,
          showShippingAddressFormError,
          submitTitle,
        } = fieldRenderProps;
        const submittedOnce = Object.keys(submittedValues).length > 0;
        const pristineSinceLastSubmit = submittedOnce && isEqual(values, submittedValues);
        const classes = classNames(rootClassName || css.root, className);

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            {showHeading ? <H3>{title}</H3> : null}

            {showShippingAddressFormError ? (
              <p className={css.error}>
                <FormattedMessage id="ShippingAddressForm.formError" />
              </p>
            ) : null}

            <ShippingAddressFields
              formId={formId}
              form={form}
              values={values}
              locale={intl.locale}
            />

            {saveError ? (
              <p className={css.error}>
                <FormattedMessage id="ShippingAddressForm.saveError" />
              </p>
            ) : null}

            <Button
              className={css.submitButton}
              type="submit"
              inProgress={saveInProgress}
              ready={pristineSinceLastSubmit}
            >
              {submitTitle || defaultSubmitTitle}
            </Button>
          </Form>
        );
      }}
    />
  );
};

ShippingAddressFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  successCallback: func,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const { saveInProgress, saveError } = state.ShippingAddressForm || {};
  return {
    initialValues: shippingAddressFromUser(currentUser),
    saveInProgress,
    saveError,
  };
};

const mapDispatchToProps = dispatch => ({
  onSubmit: address => dispatch(saveShippingAddress(address)),
});

const ShippingAddressForm = compose(connect(mapStateToProps, mapDispatchToProps))(
  ShippingAddressFormComponent
);

export default ShippingAddressForm;
